import { ChevronDown, ChevronUp, Network } from "lucide-react";
import * as React from "react";

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import { Badge, cn } from "@ando/ui";

import type { HypermemTenantSortColumn } from "@ando/core";

import { useHypermemTenants } from "@/features/admin/hooks/useAdminHypermem";
import { formatInt, formatTimestamp } from "@/features/admin/lib/adminFormat";

import { HmEmpty, HmError, HmLoading } from "./HmStates";

/**
 * Platform-wide comparative view across tenants (v2 port, superadmin only).
 *
 * BE: ``GET /admin/hypermem/state/by-tenant`` (via ``useHypermemTenants``).
 * Low-PII: counts + tenant labels only — no fact bodies, no user identifiers.
 * Sortable columns (server-side sort: ``sortBy``/``sortDir`` are passed to the
 * hook), pagination (``limit``/``offset``) and row-click → ``onSelectTenant``
 * so the parent shell can scope the other tabs to the chosen tenant.
 */

type ColumnKey = HypermemTenantSortColumn | "tenant_id";

interface ColumnDef {
  key: ColumnKey;
  label: MessageDescriptor;
  sortable: boolean;
  numeric?: boolean;
}

const COLUMNS: readonly ColumnDef[] = [
  { key: "organization_name", label: msg`Tenant`, sortable: true },
  {
    key: "episodic_facts_count",
    label: msg`Episodic`,
    sortable: true,
    numeric: true,
  },
  {
    key: "semantic_facts_count",
    label: msg`Semantic`,
    sortable: true,
    numeric: true,
  },
  {
    key: "salience_scores_count",
    label: msg`Salience`,
    sortable: true,
    numeric: true,
  },
  {
    key: "pending_workflow_suggestions",
    label: msg`Workflow sugg.`,
    sortable: true,
    numeric: true,
  },
  {
    key: "pending_nudges_count",
    label: msg`Nudge attivi`,
    sortable: true,
    numeric: true,
  },
  { key: "last_user_activity", label: msg`Ultima attività`, sortable: true },
] as const;

const PAGE_LIMIT = 100;

export function HmTenantsTab({
  onSelectTenant,
}: {
  onSelectTenant: (tenantId: string) => void;
}): React.ReactElement {
  const { t, i18n } = useLingui();
  const [sortBy, setSortBy] = React.useState<HypermemTenantSortColumn>("episodic_facts_count");
  const [sortDir, setSortDir] = React.useState<"asc" | "desc">("desc");
  const [offset, setOffset] = React.useState(0);

  const { data, isLoading, isError, refetch } = useHypermemTenants({
    limit: PAGE_LIMIT,
    offset,
    sortBy,
    sortDir,
  });

  const onSort = React.useCallback(
    (col: HypermemTenantSortColumn) => {
      if (col === sortBy) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(col);
        setSortDir("desc");
      }
      setOffset(0);
    },
    [sortBy],
  );

  const onHeaderKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTableCellElement>, col: HypermemTenantSortColumn) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSort(col);
      }
    },
    [onSort],
  );

  const rows = data?.tenants ?? [];
  const total = data?.total_tenants ?? 0;
  const sortLabelMsg = COLUMNS.find((c) => c.key === sortBy)?.label;
  const sortLabel = sortLabelMsg ? i18n._(sortLabelMsg) : sortBy;
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = offset + rows.length;
  const hasPrev = offset > 0;
  const hasNext = pageEnd < total;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-ink-1">
          <Network className="h-5 w-5 text-ando-orange" aria-hidden />
          <h3 className="font-semibold text-sm">
            <Trans>Vista comparativa per tenant</Trans>
          </h3>
        </div>
        {!isLoading && !isError && (
          <p className="text-ink-3 text-xs">
            <Trans>
              {formatInt(total)} tenant nel platform · ordinamento per{" "}
              <strong className="font-medium text-ink-2">{sortLabel}</strong> ({sortDir})
            </Trans>
          </p>
        )}
      </div>

      {isLoading && <HmLoading label={<Trans>Caricamento tenant…</Trans>} />}

      {isError && (
        <HmError
          message={<Trans>Impossibile caricare la vista comparativa dei tenant.</Trans>}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <HmEmpty label={<Trans>Nessun tenant trovato.</Trans>} />
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-hairline bg-cream/40">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-hairline border-b text-left text-ink-3">
                {COLUMNS.map((col) => {
                  const isSorted = col.sortable && col.key === sortBy;
                  const ariaSort: "ascending" | "descending" | "none" = isSorted
                    ? sortDir === "asc"
                      ? "ascending"
                      : "descending"
                    : "none";
                  return (
                    <th
                      key={col.key}
                      scope="col"
                      className={cn(
                        "px-3 py-2 font-medium text-xs",
                        col.numeric ? "text-right" : "text-left",
                        col.sortable && "cursor-pointer select-none hover:text-ink-1",
                      )}
                      aria-sort={col.sortable ? ariaSort : undefined}
                      role={col.sortable ? "button" : undefined}
                      tabIndex={col.sortable ? 0 : undefined}
                      onClick={
                        col.sortable ? () => onSort(col.key as HypermemTenantSortColumn) : undefined
                      }
                      onKeyDown={
                        col.sortable
                          ? (e) => onHeaderKeyDown(e, col.key as HypermemTenantSortColumn)
                          : undefined
                      }
                    >
                      <span
                        className={cn(
                          "inline-flex items-center gap-1",
                          col.numeric && "flex-row-reverse",
                        )}
                      >
                        {i18n._(col.label)}
                        {isSorted &&
                          (sortDir === "asc" ? (
                            <ChevronUp className="h-3 w-3" aria-hidden />
                          ) : (
                            <ChevronDown className="h-3 w-3" aria-hidden />
                          ))}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.tenant_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectTenant(row.tenant_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectTenant(row.tenant_id);
                    }
                  }}
                  className="cursor-pointer border-hairline border-b transition-colors last:border-0 hover:bg-ink-3/5"
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-ink-1">
                      {row.organization_name ?? "(senza nome)"}
                    </div>
                    <div className="font-mono text-ink-3 text-xs">{row.tenant_id.slice(0, 8)}…</div>
                  </td>
                  <td className="px-3 py-2 text-right text-ink-1 tabular-nums">
                    {formatInt(row.episodic_facts_count)}
                  </td>
                  <td className="px-3 py-2 text-right text-ink-1 tabular-nums">
                    {formatInt(row.semantic_facts_count)}
                    {row.semantic_facts_valid_count !== row.semantic_facts_count && (
                      <Badge
                        variant="outline"
                        className="ml-2 text-xs"
                        title="Semantic facts validi"
                      >
                        {formatInt(row.semantic_facts_valid_count)} validi
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-ink-1 tabular-nums">
                    {formatInt(row.salience_scores_count)}
                  </td>
                  <td className="px-3 py-2 text-right text-ink-1 tabular-nums">
                    {row.pending_workflow_suggestions > 0 ? (
                      <Badge variant="outline">{formatInt(row.pending_workflow_suggestions)}</Badge>
                    ) : (
                      <span className="text-ink-3">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-ink-1 tabular-nums">
                    {row.pending_nudges_count > 0 ? (
                      formatInt(row.pending_nudges_count)
                    ) : (
                      <span className="text-ink-3">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-ink-2">
                    {row.last_user_activity ? (
                      formatTimestamp(row.last_user_activity)
                    ) : (
                      <span className="text-ink-3">mai</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && !isError && total > 0 && (
        <div className="flex items-center justify-between gap-3 text-ink-3 text-xs">
          <span className="tabular-nums">
            {formatInt(pageStart)}–{formatInt(pageEnd)} di {formatInt(total)}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={!hasPrev}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_LIMIT))}
              className={cn(
                "rounded-lg px-3 py-1.5 font-medium transition-colors",
                hasPrev ? "text-ink-2 hover:bg-ink-3/10" : "cursor-not-allowed text-ink-3/50",
              )}
            >
              Precedente
            </button>
            <button
              type="button"
              disabled={!hasNext}
              onClick={() => setOffset((o) => o + PAGE_LIMIT)}
              className={cn(
                "rounded-lg px-3 py-1.5 font-medium transition-colors",
                hasNext ? "text-ink-2 hover:bg-ink-3/10" : "cursor-not-allowed text-ink-3/50",
              )}
            >
              Successivo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
